import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShoppingCart,
  Plus,
  Package,
  TrendingUp,
  FileText,
  Calendar,
  DollarSign,
  User,
  Filter,
  Download,
  ChevronDown,
  ChevronUp,
  Image as ImageIcon,
  Printer,
  AlertTriangle
} from 'lucide-react';
import CreatePurchaseDialog from './CreatePurchaseDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PurchaseManagement() {
  const [purchases, setPurchases] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [loadingDetails, setLoadingDetails] = useState(new Set());
  const [loadingShoppingList, setLoadingShoppingList] = useState(false);
  
  // Shopping list states
  const [showBranchDialog, setShowBranchDialog] = useState(false);
  const [showItemSelectDialog, setShowItemSelectDialog] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedBranchName, setSelectedBranchName] = useState('');
  const [lowStockItems, setLowStockItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState(new Set());
  
  // Filters
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  useEffect(() => {
    fetchPurchases();
    fetchStats();
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/branches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBranches(response.data.branches || []);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchPurchases = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (filterSupplier) params.append('supplier', filterSupplier);
      if (filterStartDate) params.append('start_date', filterStartDate);
      if (filterEndDate) params.append('end_date', filterEndDate);
      
      const response = await axios.get(`${API}/purchases?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPurchases(response.data.purchases || []);
    } catch (error) {
      console.error('Error fetching purchases:', error);
      toast.error('Gagal memuat data pembelian');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/purchases/stats/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleCreateSuccess = () => {
    setShowCreateDialog(false);
    fetchPurchases();
    fetchStats();
    toast.success('Pembelian berhasil dibuat!');
  };

  const toggleRow = async (purchaseId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(purchaseId)) {
      newExpanded.delete(purchaseId);
    } else {
      newExpanded.add(purchaseId);
      const purchase = purchases.find((item) => item.id === purchaseId);
      if (purchase && !purchase._detailLoaded) {
        setLoadingDetails((current) => new Set(current).add(purchaseId));
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API}/purchases/${purchaseId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setPurchases((current) => current.map((item) =>
            item.id === purchaseId
              ? { ...item, ...response.data.purchase, _detailLoaded: true }
              : item
          ));
        } catch (error) {
          console.error('Error fetching purchase detail:', error);
          toast.error('Gagal memuat detail pembelian');
        } finally {
          setLoadingDetails((current) => {
            const next = new Set(current);
            next.delete(purchaseId);
            return next;
          });
        }
      }
    }
    setExpandedRows(newExpanded);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const exportToCSV = () => {
    const headers = ['Tanggal', 'No. PO', 'Supplier', 'No. Nota', 'Subtotal', 'Ongkir', 'Biaya Lain', 'Total'];
    const rows = purchases.map(p => [
      formatDate(p.purchase_date),
      p.purchase_number,
      p.supplier_name,
      p.invoice_number,
      p.subtotal,
      p.shipping_cost,
      p.other_costs,
      p.total_cost
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `purchase-history-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    
    toast.success('Data berhasil di-export');
  };

  // Open branch selection dialog
  const openBranchDialog = () => {
    setSelectedBranch('');
    setShowBranchDialog(true);
  };

  // Fetch low stock items after branch selection
  const fetchLowStockItems = async (branchId, branchName) => {
    setShowBranchDialog(false);
    setLoadingShoppingList(true);
    setSelectedBranchName(branchName);
    const LOW_STOCK_THRESHOLD = 10;
    
    try {
      const token = localStorage.getItem('token');
      
      const [productsRes, ingredientsRes] = await Promise.all([
        axios.get(`${API}/products`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/ingredients`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      let products = productsRes.data.products || [];
      let ingredients = ingredientsRes.data.ingredients || [];
      
      // Filter by branch if selected
      if (branchId) {
        products = products.filter(p => p.branch_id === branchId);
        ingredients = ingredients.filter(i => i.branch_id === branchId);
      }
      
      // Filter produk: stok rendah & BUKAN komposit
      const lowStockProducts = products.filter(p => {
        const currentStock = p.stock || 0;
        const isComposite = p.product_type === 'composite' || p.type === 'composite';
        return currentStock < LOW_STOCK_THRESHOLD && !isComposite;
      }).map((p, idx) => ({
        id: `product-${idx}`,
        type: 'Produk',
        name: p.name,
        unit: p.unit || 'pcs',
        currentStock: p.stock || 0
      }));
      
      // Filter ingredients dengan stok rendah
      const lowStockIngredients = ingredients.filter(i => {
        const currentStock = i.stock || 0;
        return currentStock < LOW_STOCK_THRESHOLD;
      }).map((i, idx) => ({
        id: `ingredient-${idx}`,
        type: 'Bahan Baku',
        name: i.name,
        unit: i.unit || 'pcs',
        currentStock: i.stock || 0
      }));
      
      const allLowStock = [...lowStockIngredients, ...lowStockProducts];
      
      if (allLowStock.length === 0) {
        toast.info(`Tidak ada item dengan stok rendah (< 10)${branchName ? ` di ${branchName}` : ''}`);
        setLoadingShoppingList(false);
        return;
      }
      
      // Set items dan select all by default
      setLowStockItems(allLowStock);
      setSelectedItems(new Set(allLowStock.map(item => item.id)));
      setShowItemSelectDialog(true);
      
    } catch (error) {
      console.error('Error fetching low stock items:', error);
      toast.error('Gagal memuat data stok');
    } finally {
      setLoadingShoppingList(false);
    }
  };

  // Toggle item selection
  const toggleItemSelection = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  // Select/Deselect all
  const toggleSelectAll = () => {
    if (selectedItems.size === lowStockItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(lowStockItems.map(item => item.id)));
    }
  };

  // Print selected items
  const printSelectedItems = () => {
    const itemsToPrint = lowStockItems.filter(item => selectedItems.has(item.id));
    
    if (itemsToPrint.length === 0) {
      toast.error('Pilih minimal 1 item untuk dicetak');
      return;
    }
    
    setShowItemSelectDialog(false);
    
    const today = new Date().toLocaleDateString('id-ID', { 
      day: '2-digit', month: 'long', year: 'numeric' 
    });
    const time = new Date().toLocaleTimeString('id-ID', { 
      hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Jakarta' 
    });
    
    const printWindow = window.open('', '_blank');
      
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Daftar Belanja - ${today}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            @page { size: A4; margin: 10mm 15mm; }
            body { 
              font-family: Arial, sans-serif; 
              font-size: 10pt; 
              line-height: 1.3; 
              padding: 10px;
            }
            .header {
              text-align: center;
              border-bottom: 2px solid #000;
              padding-bottom: 10px;
              margin-bottom: 15px;
            }
            .header h1 {
              font-size: 16pt;
              margin-bottom: 5px;
            }
            .header .date {
              font-size: 10pt;
              color: #666;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
              }
              th, td {
                border: 1px solid #333;
                padding: 6px 8px;
                text-align: left;
              }
              th {
                background: #f0f0f0;
                font-weight: bold;
                font-size: 9pt;
              }
              td { font-size: 9pt; }
              .type-badge {
                display: inline-block;
                padding: 2px 6px;
                border-radius: 3px;
                font-size: 8pt;
                font-weight: bold;
              }
              .type-produk { background: #dbeafe; color: #1e40af; }
              .type-bahan { background: #fef3c7; color: #92400e; }
              .text-right { text-align: right; }
              .text-center { text-align: center; }
              .need-buy { font-weight: bold; color: #dc2626; }
              .checkbox { width: 20px; height: 20px; border: 2px solid #333; }
              .footer {
                margin-top: 20px;
                padding-top: 10px;
                border-top: 1px solid #ccc;
                font-size: 8pt;
                color: #666;
                text-align: center;
              }
              .notes-section {
                margin-top: 15px;
                padding: 10px;
                border: 1px solid #ccc;
              }
              .notes-section h3 {
                font-size: 10pt;
                margin-bottom: 8px;
              }
              .notes-line {
                border-bottom: 1px solid #999;
                height: 20px;
                margin-bottom: 5px;
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1> DAFTAR BELANJA STOK</h1>
              ${selectedBranchName ? `<div class="date" style="font-weight:bold; font-size:12pt;">Cabang: ${selectedBranchName}</div>` : '<div class="date" style="font-weight:bold;">Semua Cabang</div>'}
              <div class="date">Tanggal: ${today} | Waktu: ${time} WIB</div>
              <div class="date" style="margin-top:5px;">
                Total ${itemsToPrint.length} item
              </div>
            </div>
            
            <table>
              <thead>
                <tr>
                  <th style="width:5%;" class="text-center"></th>
                  <th style="width:12%;">Tipe</th>
                  <th style="width:43%;">Nama Item</th>
                  <th style="width:15%;" class="text-right">Stok Sisa</th>
                  <th style="width:25%;" class="text-right">Beli</th>
                </tr>
              </thead>
              <tbody>
                ${itemsToPrint.map((item, idx) => `
                  <tr>
                    <td class="text-center"><div class="checkbox"></div></td>
                    <td>
                      <span class="type-badge ${item.type === 'Produk' ? 'type-produk' : 'type-bahan'}">
                        ${item.type === 'Produk' ? 'Produk' : 'Bahan'}
                      </span>
                    </td>
                    <td>${item.name}</td>
                    <td class="text-right need-buy">${item.currentStock} ${item.unit}</td>
                    <td class="text-right">_______ ${item.unit}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            
            <div class="notes-section">
              <h3>Catatan Belanja:</h3>
              <div class="notes-line"></div>
              <div class="notes-line"></div>
              <div class="notes-line"></div>
            </div>
            
            <div class="footer">
              Dicetak dari Rimuru Game Suite | ${today} ${time} WIB
            </div>
          </body>
        </html>
      `);
      
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 300);
    
    toast.success(`Daftar belanja dengan ${itemsToPrint.length} item siap dicetak`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShoppingCart className="h-7 w-7 text-blue-600" />
            Pembelian Stok
          </h2>
          <p className="text-sm text-gray-600 mt-1">Kelola pembelian bahan baku dan tracking HPP</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={openBranchDialog}
            disabled={loadingShoppingList}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-all font-semibold disabled:opacity-50"
          >
            {loadingShoppingList ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <Printer className="h-5 w-5" />
            )}
            Print Daftar Belanja
          </button>
          <button
            onClick={() => setShowCreateDialog(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-semibold"
          >
            <Plus className="h-5 w-5" />
            Buat Pembelian
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <Package className="h-8 w-8 opacity-80" />
              <span className="text-sm opacity-80">Total Pembelian</span>
            </div>
            <div className="text-3xl font-bold">{stats.total_purchases}</div>
            <div className="text-sm opacity-80 mt-1">Transaksi</div>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="h-8 w-8 opacity-80" />
              <span className="text-sm opacity-80">Total Pengeluaran</span>
            </div>
            <div className="text-2xl font-bold">{formatCurrency(stats.total_spent)}</div>
            <div className="text-sm opacity-80 mt-1">Keseluruhan</div>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="h-8 w-8 opacity-80" />
              <span className="text-sm opacity-80">Rata-rata</span>
            </div>
            <div className="text-2xl font-bold">{formatCurrency(stats.average_per_purchase)}</div>
            <div className="text-sm opacity-80 mt-1">Per Transaksi</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-5 w-5 text-gray-500" />
          <span className="font-semibold text-gray-700">Filter Data</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            type="text"
            placeholder="Cari supplier..."
            value={filterSupplier}
            onChange={(e) => setFilterSupplier(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <input
            type="date"
            value={filterStartDate}
            onChange={(e) => setFilterStartDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <input
            type="date"
            value={filterEndDate}
            onChange={(e) => setFilterEndDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            onClick={fetchPurchases}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-semibold"
          >
            Terapkan Filter
          </button>
        </div>
      </div>

      {/* Export Button */}
      <div className="flex justify-end">
        <button
          onClick={exportToCSV}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all font-semibold"
        >
          <Download className="h-4 w-4" />
          Export CSV
        </button>
      </div>

      {/* Purchases Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  No. PO
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Tanggal
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Supplier
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  No. Nota
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Items
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {purchases.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    <Package className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>Belum ada data pembelian</p>
                  </td>
                </tr>
              ) : (
                purchases.map((purchase) => {
                  const isExpanded = expandedRows.has(purchase.id);
                  
                  return (
                    <React.Fragment key={purchase.id}>
                      <tr className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => toggleRow(purchase.id)}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {isExpanded ? (
                              <ChevronUp className="h-4 w-4 text-blue-600" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-gray-400" />
                            )}
                            <span className="text-sm font-medium text-blue-600">{purchase.purchase_number}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(purchase.purchase_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-gray-400" />
                            <span className="text-sm font-medium text-gray-900">{purchase.supplier_name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-600">{purchase.invoice_number}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {purchase.items?.length || 0} item
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <span className="text-sm font-bold text-gray-900">{formatCurrency(purchase.total_cost)}</span>
                        </td>
                      </tr>

                      {/* Expanded Row */}
                      {isExpanded && (
                        <tr className="bg-blue-50">
                          <td colSpan="6" className="px-6 py-4">
                            <div className="bg-white rounded-lg p-4 border border-blue-200">
                              <h4 className="font-bold text-gray-800 mb-3">Detail Pembelian</h4>
                              {loadingDetails.has(purchase.id) && (
                                <p className="text-sm text-gray-500 mb-3">Memuat detail nota...</p>
                              )}
                              
                              {/* Items List */}
                              <div className="mb-4">
                                <div className="text-sm font-semibold text-gray-600 mb-2">Items:</div>
                                <div className="space-y-2">
                                  {purchase.items?.map((item, idx) => (
                                    <div key={idx} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                                      <div className="flex items-center gap-3">
                                        <span className="text-xs font-semibold text-gray-500 bg-white px-2 py-1 rounded">
                                          {item.quantity} {item.unit}
                                        </span>
                                        <span className="text-sm font-medium text-gray-900">{item.item_name || item.ingredient_name}</span>
                                      </div>
                                      <div className="text-right">
                                        <div className="text-xs text-gray-500">
                                          @ {formatCurrency(item.price_per_unit || item.purchase_price)}/{item.unit}
                                        </div>
                                        <div className="text-sm font-bold text-gray-700">
                                          {formatCurrency(item.total_price || item.subtotal)}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Cost Breakdown */}
                              <div className="border-t border-gray-200 pt-3 space-y-2">
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">Subtotal Items:</span>
                                  <span className="font-medium">{formatCurrency(purchase.subtotal)}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">Ongkir:</span>
                                  <span className="font-medium">{formatCurrency(purchase.shipping_cost)}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">Biaya Lain:</span>
                                  <span className="font-medium">{formatCurrency(purchase.other_costs)}</span>
                                </div>
                                <div className="flex justify-between text-base font-bold border-t pt-2">
                                  <span className="text-gray-900">Total:</span>
                                  <span className="text-blue-600">{formatCurrency(purchase.total_cost)}</span>
                                </div>
                              </div>

                              {/* Notes & Receipt */}
                              {(purchase.notes || purchase.receipt_image) && (
                                <div className="mt-4 border-t border-gray-200 pt-3">
                                  {purchase.notes && (
                                    <div className="mb-3">
                                      <div className="text-sm font-semibold text-gray-600 mb-1">Catatan:</div>
                                      <p className="text-sm text-gray-700">{purchase.notes}</p>
                                    </div>
                                  )}
                                  {purchase.receipt_image && (
                                    <div>
                                      <div className="text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                                        <ImageIcon className="h-4 w-4" />
                                        Foto Nota:
                                      </div>
                                      <img
                                        src={purchase.receipt_image}
                                        alt="Nota"
                                        className="max-w-md rounded-lg border border-gray-300"
                                      />
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Purchase Dialog */}
      {showCreateDialog && (
        <CreatePurchaseDialog
          open={showCreateDialog}
          onClose={() => setShowCreateDialog(false)}
          onSuccess={handleCreateSuccess}
        />
      )}

      {/* Branch Selection Dialog for Shopping List */}
      {showBranchDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Printer className="h-5 w-5 text-amber-500" />
              Print Daftar Belanja
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pilih Cabang
              </label>
              <select
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              >
                <option value="">-- Semua Cabang --</option>
                {branches.map(branch => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Kosongkan untuk semua cabang
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowBranchDialog(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Batal
              </button>
              <button
                onClick={() => {
                  const branch = branches.find(b => b.id === selectedBranch);
                  fetchLowStockItems(selectedBranch, branch?.name || '');
                }}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors flex items-center gap-2"
              >
                Lanjut
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Item Selection Dialog */}
      {showItemSelectDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col">
            <div className="p-4 border-b">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Printer className="h-5 w-5 text-amber-500" />
                Pilih Item untuk Dicetak
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {selectedBranchName || 'Semua Cabang'} - {lowStockItems.length} item stok rendah ({'<'} 10)
              </p>
            </div>
            
            <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedItems.size === lowStockItems.length}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 rounded border-gray-300 text-amber-500 focus:ring-amber-500"
                />
                <span className="text-sm font-medium">Pilih Semua</span>
              </label>
              <span className="text-sm text-gray-600">
                {selectedItems.size} dari {lowStockItems.length} dipilih
              </span>
            </div>

            <div className="flex-1 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 sticky top-0 z-10">
                  <tr>
                    <th className="p-3 text-left w-12 border-b"></th>
                    <th className="p-3 text-left w-24 border-b">Tipe</th>
                    <th className="p-3 text-left border-b">Nama Item</th>
                    <th className="p-3 text-right w-24 border-b">Stok</th>
                  </tr>
                </thead>
                <tbody>
                  {lowStockItems.map(item => (
                    <tr 
                      key={item.id} 
                      className={`border-b cursor-pointer hover:bg-amber-50 ${selectedItems.has(item.id) ? 'bg-amber-50' : ''}`}
                      onClick={() => toggleItemSelection(item.id)}
                    >
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={selectedItems.has(item.id)}
                          onChange={() => toggleItemSelection(item.id)}
                          className="w-4 h-4 rounded border-gray-300 text-amber-500 focus:ring-amber-500"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${item.type === 'Produk' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {item.type === 'Produk' ? 'Produk' : 'Bahan'}
                        </span>
                      </td>
                      <td className="p-3 font-medium">{item.name}</td>
                      <td className="p-3 text-right text-red-600 font-semibold">{item.currentStock} {item.unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="p-4 border-t flex justify-end gap-3">
              <button
                onClick={() => setShowItemSelectDialog(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Batal
              </button>
              <button
                onClick={printSelectedItems}
                disabled={selectedItems.size === 0}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Printer className="h-4 w-4" />
                Print ({selectedItems.size} item)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
